import React, { useContext } from 'react';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import { useSelector, shallowEqual } from 'react-redux';
import { Box, Grid, Button, RadioGroup } from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import Stripe from 'stripe';
import { PortalConfigContext } from '../../context';
import AccountTypeSelect from './AccountTypeSelect';
import { RootState } from '../../store/reduxTypes';
import { CognitoUser } from '../../store/user/types';
import { PaymentInfo } from '../../store/payments/types';
import UsersClient from '../../clients/UsersClient';
import { ACCOUNT_TYPE } from '../../constants';
import { BaseTextField } from '../TextField';
import BaseTypography from '../Text/BaseTypography';
import { GraySmall } from '../../theme/colors';

import { AccountStatusData, VerificationStatus } from './VerificationStatus';
import { GetStripeAccountVerificationStatus } from '../../utils/BillingUtils';

interface CountryOptionType {
  code: string;
  label: string;
}

const expressCoOptions = [
  { label: 'Australia', code: 'AU' },
  { label: 'Belgium', code: 'BE' },
  { label: 'Canada', code: 'CA' },
  { label: 'Denmark', code: 'DK' },
  { label: 'Finland', code: 'FI' },
  { label: 'France', code: 'FR' },
  { label: 'Germany', code: 'DE' },
  { label: 'Greece', code: 'GR' },
  { label: 'Ireland', code: 'IE' },
  { label: 'Mexico', code: 'MX' },
  { label: 'Netherlands', code: 'NL' },
  { label: 'New Zealand', code: 'NZ' },
  { label: 'Norway', code: 'NO' },
  { label: 'Spain', code: 'ES' },
  { label: 'Sweden', code: 'SE' },
  { label: 'Switzerland', code: 'CH' },
  { label: 'Thailand', code: 'TH' },
  { label: 'United Arab Emirates', code: 'AE' },
  { label: 'United Kingdom', code: 'GB' },
  { label: 'United States', code: 'US' },
];

const standardCoOptions = [...expressCoOptions];
// Index to insert India/Indonesia in alphabetical order
const standardOnlyOptionsInsertPosition = 8;
standardCoOptions.splice(
  standardOnlyOptionsInsertPosition,
  0,
  { label: 'India', code: 'IN' },
  { label: 'Indonesia', code: 'ID' },
);

const getDefaultCountryCode = () => {
  const localeString = navigator.language;
  let components = localeString.split('_');
  if (components.length === 2) {
    return components.pop();
  }
  components = localeString.split('-');
  if (components.length === 2) {
    return components.pop();
  }
  return localeString;
};

const getCountryOptions = (val: string) =>
  val === 'express' ? expressCoOptions : standardCoOptions;

interface PaymentState {
  loading: boolean;
  cognitoUser: CognitoUser | null;
  paymentInfo: PaymentInfo;
}

interface StripeConnectFormValueType {
  accountType: string;
  businessName: string;
  country: string;
}

export const StripeConnectForm: React.FC = () => {
  const portalConfig = useContext(PortalConfigContext);
  const { loading, cognitoUser, paymentInfo } = useSelector<
    RootState,
    PaymentState
  >(
    state => ({
      loading: state.settings.loading,
      cognitoUser: state.user.instance,
      paymentInfo: state.payments.paymentInfo,
    }),
    shallowEqual,
  );
  const backToPortalDestinationPath = useSelector(
    (state: RootState) => state.ui.backToPortalDestinationPath,
  );

  const { id, address: paymentAddress, accountType, businessName, accountId } =
    paymentInfo || {};

  const { country } = paymentAddress || {
    country: getDefaultCountryCode()?.toUpperCase(),
  };

  const [accountStatus, setAccountStatus] = React.useState<AccountStatusData>({
    accountType: '',
    loginUrl: '',
    verificationStatus: '',
    accountName: '',
    currency: '',
  });

  const [isVerifying, setIsVerifying] = React.useState(true);

  const checkAccount = async (stripeAccountId: string) => {
    if (stripeAccountId === 'restricted') {
      // if accountID is restricted, then we don't need load account and check
      // any further status and directly set status to restricted
      setAccountStatus({
        accountType: '',
        loginUrl: '',
        verificationStatus: 'restricted',
        accountName: '',
        currency: '',
      });
      setIsVerifying(false);
      return;
    }

    setIsVerifying(true);
    const result = await UsersClient.getAccount(stripeAccountId);
    if (result && result.data) {
      let stripeLoginLink = '';
      const {
        payouts_enabled,
        type,
        requirements,
        external_accounts,
        default_currency,
      } = result.data;

      if (type === ACCOUNT_TYPE.EXPRESS) {
        const loginUrlResult = await UsersClient.getAccountLoginLink(
          stripeAccountId,
        );
        if (loginUrlResult && loginUrlResult.data) {
          stripeLoginLink = loginUrlResult.data.url;
        }
      }

      let accountName = 'Connected accounts';
      if (external_accounts && external_accounts.data.length > 0) {
        const externalAccount = external_accounts.data[0];
        if (externalAccount.object === 'bank_account') {
          accountName = externalAccount.bank_name || 'Bank';
        } else if (externalAccount.object === 'card') {
          accountName = externalAccount.brand;
        }
      }
      setAccountStatus({
        accountType: type,
        loginUrl: stripeLoginLink,
        verificationStatus: GetStripeAccountVerificationStatus(
          payouts_enabled,
          requirements as Stripe.Account.Requirements,
        ),
        accountName,
        currency: default_currency,
      });
    }
    setIsVerifying(false);
  };

  React.useEffect(() => {
    if (accountId && !loading) {
      checkAccount(accountId);
    } else {
      setAccountStatus({
        accountType: '',
        loginUrl: '',
        verificationStatus: '',
        accountName: '',
        currency: '',
      });
      setIsVerifying(false);
    }
  }, [accountId, loading]);

  const renderFormGrid = ({
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    setFieldValue,
  }: FormikProps<StripeConnectFormValueType>) => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <RadioGroup
            data-testid="account-type-select-radio-group-id"
            aria-label="accountType"
            name="accountType"
            value={values.accountType}
            onChange={handleChange}
          >
            <AccountTypeSelect
              value="express"
              label={
                <>
                  <BaseTypography fontType="13Medium" component="span">
                    Express
                  </BaseTypography>{' '}
                  <BaseTypography component="span" style={{ color: GraySmall }}>
                    - Enter your business and banking information to receive
                    payments without having to manage a Stripe account
                  </BaseTypography>
                </>
              }
            />
            <AccountTypeSelect
              value="standard"
              label={
                <>
                  <BaseTypography fontType="13Medium" component="span">
                    Standard
                  </BaseTypography>{' '}
                  <BaseTypography component="span" style={{ color: GraySmall }}>
                    - Create a new Stripe account or connect your existing
                    account that you manage
                  </BaseTypography>
                </>
              }
            />
          </RadioGroup>
        </Grid>

        <Grid item xs={12}>
          <BaseTextField
            sizeVariant="medium"
            error={Boolean(touched.businessName && errors.businessName)}
            fullWidth
            helperText={touched.businessName && errors.businessName}
            label="Registered Company Name"
            name="businessName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.businessName}
            variant="outlined"
            placeholder="Infinity & Beyond LLC"
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="country-code-select"
            onChange={(_, value) =>
              setFieldValue('country', (value as CountryOptionType)?.code || '')
            }
            value={
              getCountryOptions(values.accountType).find(
                option => option.code === values.country,
              ) || null
            }
            options={getCountryOptions(values.accountType)}
            getOptionLabel={option => option.label}
            renderInput={params => (
              <BaseTextField
                {...params}
                sizeVariant="medium"
                error={Boolean(touched.country && errors.country)}
                fullWidth
                data-testid="country-select-id"
                helperText={touched.country && errors.country}
                label={
                  <>
                    <BaseTypography fontType="13Medium" component="span">
                      Country
                    </BaseTypography>
                    <BaseTypography
                      component="span"
                      style={{ color: GraySmall }}
                    >
                      {' '}
                      - The currency of your bank account will be used for
                      billing clients
                    </BaseTypography>
                  </>
                }
                onBlur={handleBlur}
                variant="outlined"
                placeholder="Select billing country"
                inputProps={{
                  ...params.inputProps,
                  isAutoComplete: true,
                }}
              />
            )}
          />
        </Grid>
      </Grid>
      <Box mt={4.5} display="flex" justifyContent="flex-start">
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          color="primary"
        >
          Connect
        </Button>
      </Box>
    </>
  );

  return (
    <Formik
      enableReinitialize
      initialValues={{
        businessName: businessName || '',
        country: country || '',
        accountType: accountType || ACCOUNT_TYPE.EXPRESS,
      }}
      validationSchema={Yup.object().shape({
        businessName: Yup.string().required('Required'),
        accountType: Yup.string().oneOf([
          ACCOUNT_TYPE.STANDARD,
          ACCOUNT_TYPE.EXPRESS,
        ]),
        country: Yup.string()
          .length(2, 'Two-letter country code (e.g., US or CA).')
          .required('Required'),
      })}
      onSubmit={async values => {
        if (!cognitoUser) {
          return;
        }

        const stripeOauthUrl = await UsersClient.GetStripeOauthUrl(
          {
            id,
            email: cognitoUser.attributes.email,
            ...values,
          },
          portalConfig,
          backToPortalDestinationPath,
        );
        window.location.assign(stripeOauthUrl.data);
      }}
    >
      {formProps =>
        loading || isVerifying ? (
          <Skeleton variant="text" />
        ) : (
          <form onSubmit={formProps.handleSubmit}>
            {!accountId ? (
              renderFormGrid(formProps)
            ) : (
              <VerificationStatus statusData={accountStatus} />
            )}
          </form>
        )
      }
    </Formik>
  );
};
